import React, { useState, useEffect, useMemo, useCallback } from 'react'
import '../../css/Mypage/editInfo.css'
import { ReactComponent as RadioDefault } from '../../assets/icons/svg/radioBtnOutLine.svg'
import { ReactComponent as RadioFilled } from '../../assets/icons/svg/radioBtnFilled.svg'
import CommonModal from '../../components/Common-components/Modal/modal';
import { apiPost } from '../../utils/api';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import PhoneCodeSelect from '../Common-components/region/PhoneCodeSelect';
import countries from 'i18n-iso-countries';
import ko from 'i18n-iso-countries/langs/ko.json';
import en from 'i18n-iso-countries/langs/en.json';
import * as countryCodes from 'country-codes-list';

countries.registerLocale(ko);
countries.registerLocale(en);


// 생년월일 포맷 변환(저장값은 '/' 기반)
const normalizeBirthForDisplay = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/[^0-9]/g, '');
  if (digits.length !== 8) return String(raw).replace(/\//g, '-');
  const yyyy = digits.slice(0, 4);
  const mm = digits.slice(4, 6);
  const dd = digits.slice(6, 8);
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeBirthForSubmit = (raw) => {
  if (!raw) return '';
  return String(raw).replace(/-/g, '/');
};

// 미니 컴포넌트: 일반 텍스트 입력 필드
const TextInput = ({ label, id, type, value, onChange, required, placeholder, readOnly = false, inputmode }) => (
  <div className='form-group'>
    <label htmlFor={id}>{label}</label>
    <input
      type={type}
      id={id}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      readOnly={readOnly}
      inputMode={inputmode}
      autoComplete="off"
    />
  </div>
);

// 미니 컴포넌트: 라디오 버튼
const RadioInput = ({ label, name, value, checked, onChange, inputId }) => (
  <label className='radio-custom-label' htmlFor={inputId}>
    <input
      type='radio'
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      id={inputId}
      style={{ display: 'none' }} // 기본 라디오 버튼 숨기기
    />
    {checked ? <RadioFilled className='radio-icon' /> : <RadioDefault className='radio-icon' />}
    <span>{label}</span>
  </label>
);

// 미니 컴포넌트: 체크박스
const CheckboxInput = ({ label, id, checked, onChange, required = false, modalTitle, modalContent, className }) => (
  <div className='checkbox-group'>
    <div className={`group${required ? ' required' : ''}`}>
      <input
        type='checkbox'
        id={id}
        checked={checked}
        onChange={onChange}
        required={required}
      />
      <label htmlFor={id} className={className}>{label}</label>
    </div>
    {modalContent && (
      <CommonModal
        trigger={<span className='view-details-link'>보기</span>}
        title={modalTitle || label}
        showFooter={false}
      >
        {modalContent}
      </CommonModal>
    )}
  </div>
);

function EditInfo({ data }) {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [birth, setBirth] = useState('');
  const [gender, setGender] = useState('');
  const [phoneCode, setPhoneCode] = useState('+82');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreements, setAgreements] = useState({});

  const [isInitialized, setIsInitialized] = useState(false);
  const { setUser } = useUser();         // 로그아웃 처리용
  const navigate = useNavigate();        // 탈퇴 후 홈 이동용

  const [allAgreed, setAllAgreed] = useState(false);

  // 국가/전화 코드 목록은 회원가입 폼과 동일하게 country-codes-list를 사용
  const phoneOptions = useMemo(() => {
    const cc = (countryCodes && countryCodes.customList)
      ? countryCodes
      : (countryCodes && countryCodes.default && countryCodes.default.customList)
        ? countryCodes.default
        : null;
    if (!cc) return [];
    const rawMap = cc.customList('countryCode', '{countryNameEn}|{countryCallingCode}');
    const list = Object.entries(rawMap).map(([alpha2, str]) => {
      const [nameEnFromList, calling] = str.split('|');
      const nameKo = countries.getName(alpha2, 'ko');
      const nameEnIso = countries.getName(alpha2, 'en');
      const name = nameKo || nameEnIso || nameEnFromList || alpha2;
      const dial = calling ? `+${calling}` : '';
      return dial ? { alpha2, name, dial } : null;
    }).filter(Boolean);

    const priority = ['KR', 'US', 'JP'];
    const prio = list.filter(o => priority.includes(o.alpha2));
    const rest = list.filter(o => !priority.includes(o.alpha2)).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    return [...prio, ...rest];
  }, []);

  // 국가 코드(ISO) → 전화 다이얼 문자열(+82 등) 매핑
  const findDialForCountry = useCallback((country) => {
    if (!country) return '+82';
    const upper = String(country).toUpperCase();
    const matched = phoneOptions.find(opt => opt.alpha2 === upper);
    return matched ? matched.dial : '+82';
  }, [phoneOptions]);

  // 기존 DB 전화번호를 국제전화번호/나머지 번호로 분리
  const splitPhoneNumber = useCallback((rawPhone) => {
    const fallbackDial = '+82';
    if (!rawPhone) {
      return { dial: fallbackDial, number: '' };
    }
    const normalized = String(rawPhone).replace(/[\s-]/g, '');
    const sortedOptions = [...phoneOptions].sort((a, b) => b.dial.length - a.dial.length);
    const matched = sortedOptions.find(opt => normalized.startsWith(opt.dial));

    if (matched) {
      const rest = normalized.slice(matched.dial.length);
      return { dial: matched.dial, number: rest };
    }

    if (normalized.startsWith('+')) {
      const manualDial = normalized.match(/^\+\d{1,4}/);
      if (manualDial) {
        const rest = normalized.slice(manualDial[0].length);
        return { dial: manualDial[0], number: rest };
      }
    }

    const fallbackNumber = normalized.startsWith('+') ? normalized.replace(/^\+/, '') : normalized;
    return { dial: fallbackDial, number: fallbackNumber };
  }, [phoneOptions]);

  // 최초 로딩 시 전달받은 프로필 데이터를 입력폼 상태로 변환
  useEffect(() => {
    if (!data || isInitialized) return;

    setName(data.name || '');
    setId(data.id || '');
    setBirth(normalizeBirthForDisplay(data.birth));
    setGender(data.gender || '');

    const byCountry = data?.country ? findDialForCountry(data.country) : null;
    const fromPhone = splitPhoneNumber(data.tel);
    const nextDial = byCountry || fromPhone.dial;
    setPhoneCode(nextDial || '+82');
    setPhoneNumber(fromPhone.number || '');
    setAgreements({
      required1: data?.requiredAgreements?.required1 || false,
      required2: data?.requiredAgreements?.required2 || false,
      required3: data?.requiredAgreements?.required3 || false,
      required4: data?.requiredAgreements?.required4 || false,
      optional1: data?.optionalAgreements?.optional1 || false,
      optional2: data?.optionalAgreements?.optional2 || false,
    });

    setIsInitialized(true); //1번만 초기화
  }, [data, isInitialized, findDialForCountry, splitPhoneNumber]);

  const requiredAgreementsData = useMemo(() => [
    { id: 'required1', label: '이용약관', content: '이용약관 상세 내용입니다.' },
    { id: 'required2', label: '개인정보 수집 및 이용동의', content: '개인정보 수집 및 이용동의 상세 내용입니다.' },
    { id: 'required3', label: '개인정보 취급 위탁', content: '개인정보 취급 위탁 상세 내용입니다.' },
    { id: 'required4', label: '포인트 약관', content: '포인트 약관 상세 내용입니다.' },
  ], []);

  const optionalAgreementsData = useMemo(() => [
    { id: 'optional1', label: '개인정보 3자 제공', content: '개인정보 3자 제공 상세 내용입니다.' },
    { id: 'optional2', label: '마케팅 목적 수집 및 활용 동의', content: '마케팅 목적 수집 및 활용 동의 상세 내용입니다.' },
  ], []);

  const requiredAgreementIds = useMemo(() => requiredAgreementsData.map(item => item.id), [requiredAgreementsData]);
  // 모든 약관 ID를 포함하는 배열
  const allAgreementIds = useMemo(() => [
    ...requiredAgreementsData.map(item => item.id),
    ...optionalAgreementsData.map(item => item.id),
  ], [requiredAgreementsData, optionalAgreementsData]);

  // 생년월일 입력 포맷팅 함수
  const handleBirthChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, ''); // 숫자만 추출

    if (value.length > 8) {
      value = value.slice(0, 8);
    }

    if (value.length > 4) {
      value = value.substring(0, 4) + '-' + value.substring(4);
    }
    if (value.length > 7) {
      value = value.substring(0, 7) + '-' + value.substring(7);
    }
    setBirth(value);
  };

  const [formIsValid, setFormIsValid] = useState(false);

  // 모든 필수 필드가 채워졌는지 확인하는 유효성 검사
  useEffect(() => {
    const allRequiredAgreementsChecked = requiredAgreementsData.every(item => agreements[item.id]);
    const sanitizedNumber = String(phoneNumber || '').replace(/[^0-9]/g, '');
    const hasDial = !!String(phoneCode || '').trim();
    const isValid = name && id && birth && gender && hasDial && sanitizedNumber && allRequiredAgreementsChecked;
    setFormIsValid(isValid);
  }, [name, id, birth, gender, phoneCode, phoneNumber, agreements, requiredAgreementsData]);

  // 전체 동의 체크박스 핸들러
  useEffect(() => {
    const allChecked = allAgreementIds.every(id => agreements[id]);
    setAllAgreed(allChecked);
  }, [agreements, allAgreementIds]);

  const handleAllAgreedChange = (e) => {
    const checked = e.target.checked;
    const newAgreements = {};
    allAgreementIds.forEach(id => {
      if (!checked && requiredAgreementIds.includes(id)) {
        newAgreements[id] = true;
      } else {
        newAgreements[id] = checked;
      }
    });
    setAgreements(newAgreements);
  };



  const handleAgreementChange = (e) => {
    const { id, checked } = e.target;
    if (!checked && requiredAgreementIds.includes(id)) {
      return;
    }
    setAgreements((prevAgreements) => ({
      ...prevAgreements,
      [id]: checked,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredAgreements = {};
    requiredAgreementsData.forEach(item => {
      requiredAgreements[item.id] = !!agreements[item.id];
    });

    const optionalAgreements = {};
    optionalAgreementsData.forEach(item => {
      optionalAgreements[item.id] = !!agreements[item.id];
    });

    const sanitizedNumber = String(phoneNumber || '').replace(/[^0-9]/g, '');
    const normalizedDial = String(phoneCode || '').trim() || '+82';
    const normalizedPhone = sanitizedNumber ? `${normalizedDial}${sanitizedNumber}` : '';

    const updatedUserInfo = {
      user_name: name,
      email: id,
      phone: normalizedPhone,
      company: '',
      part: '',
      rcv_sms: 'N',
      sex: gender,
      rcv_email: agreements.optional2 ? 'Y' : 'N',
      agree01: agreements.required1 ? 'Y' : 'N',
      agree02: agreements.required2 ? 'Y' : 'N',
      agree03: agreements.required3 ? 'Y' : 'N',
      agree04: agreements.required4 ? 'Y' : 'N',
      agree05: agreements.optional1 ? 'Y' : 'N',
      birthday: normalizeBirthForSubmit(birth),
    };

    // console.log('[회원정보 수정] 요청 payload', updatedUserInfo);
    try {
      const res = await apiPost('/member/update.php', updatedUserInfo);
      if (res.result === 'success') {
        alert('회원 정보가 변경되었습니다.');
      } else {
        alert('수정 실패: ' + (res.message || '서버 오류로 회정정보 수정에 실패했습니다.'));
      }
    } catch (err) {
      console.error('회원정보 수정 실패', err);
      alert('서버 오류로 회정정보 수정에 실패했습니다.');
    }
  };

  const handleWithdraw = async () => {
    // if (!window.confirm('정말로 회원을 탈퇴하시겠습니까?\n모든 예약 정보와 회원 정보가 삭제됩니다.')) return;

    try {
      const res = await apiPost('/member/out.php');
      if (res.result === 'success') {
        alert('회원 탈퇴가 완료되었습니다.');
        setUser(null);         // UserContext 초기화
        navigate('/');         // 홈으로 이동
      } else {
        alert('탈퇴 실패: ' + (res.message || '서버 오류'));
      }
    } catch (err) {
      console.error('회원 탈퇴 오류', err);
      alert(err?.response?.data?.message || '서버 오류로 탈퇴에 실패했습니다.');
    }
  };

  return (
    <div className='edit-user-info'>
      <form onSubmit={handleSubmit}>
        <TextInput
          label='아이디(이메일 주소)'
          id='id'
          type='email'
          value={id}
          onChange={(e) => setId(e.target.value)}
          required
          readOnly
        />

        <TextInput
          label='이름'
          id='name'
          type='text'
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <TextInput
          label='생년월일(예:2020-01-01)'
          id='birth'
          type='text'
          value={birth}
          onChange={handleBirthChange}
          required
          placeholder='YYYY-MM-DD'
          inputmode='numeric'
        />

        <fieldset className='form-group'>
          <legend>성별</legend>
          <div className='gender-options'>
            <RadioInput
              label='남성'
              name='gender'
              value='M'
              checked={gender === 'M'}
              onChange={(e) => setGender(e.target.value)}
              inputId='gender-male'
            />
            <RadioInput
              label='여성'
              name='gender'
              value='F'
              checked={gender === 'F'}
              onChange={(e) => setGender(e.target.value)}
              inputId='gender-female'
            />
          </div>
        </fieldset>

        <div className='form-group'>
          <label htmlFor='tel'>휴대폰 번호</label>
          <div className='tel-input-group'>
            <PhoneCodeSelect
              value={phoneCode}
              onChange={(val) => setPhoneCode(val || '+82')}
              options={phoneOptions}
              className='phone-code-select'
            />
            <TextInput
              id='tel'
              type='tel'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
              required
              placeholder='번호를 입력해 주세요.'
            />
          </div>
          <span style={{ fontWeight: '400', fontSize: "13px", lineHeight: '160%', color: '#7B8492' }}>*휴대폰 번호를 잘못 입력한 경우나 해외 번호의 경우 예약 관련 '카카오 알림 톡'을 받으실 수 없습니다.</span>
        </div>

        {/* 체크박스 동의 항목 */}
        <div className='agreement-section'>
          <CheckboxInput
            label='전체 동의하기'
            id='all-agreed'
            checked={allAgreed}
            onChange={handleAllAgreedChange}
          />

          <div style={{ height: '1px', width: '100%', backgroundColor: '#EBEBEB', margin: '2px' }}></div>

          {requiredAgreementsData.map((agreement) => (
            <CheckboxInput
              key={agreement.id}
              label={agreement.label}
              id={agreement.id}
              checked={agreements[agreement.id]}
              onChange={handleAgreementChange}
              required={true}
              modalTitle={agreement.label}
              modalContent={agreement.content}
              className="required-agreement"
            />
          ))}
          {optionalAgreementsData.map((agreement) => (
            <CheckboxInput
              key={agreement.id}
              label={agreement.label}
              id={agreement.id}
              checked={agreements[agreement.id]}
              onChange={handleAgreementChange}
              modalTitle={agreement.label}
              modalContent={agreement.content}
              className="optional-agreement"
            />
          ))}
        </div>

        <button type='submit' className='edit-info-btn btn' disabled={!formIsValid}>저장하기</button>

        <button
          type='button'
          className='secession-btn btn'
          onClick={() => alert({
            title: '와우크루즈 회원을 탈퇴하시겠습니까?',
            content: '회원을 탈퇴하시면 이용하셨던 예약 내역과 회원 정보가 사라집니다. 탈퇴를 원하시면 탈퇴하기 버튼을 눌러주세요.',
            footerContent: <button type='button'>회원 탈퇴하기</button>,
            confirmButtonText: '취소', // 기본은 확인
            action: () => { handleWithdraw() }
          })}
        >
          회원 탈퇴하기 ＞
        </button>
      </form>
    </div>
  );
}

export default EditInfo;
